import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Building2, Link2, Search, Unlink, Users, Loader2, GitMerge } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AccountSearch = ({ token, onSelect, label, prefix }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (query.trim().length < 3) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/superadmin/accounts`, {
        params: { query: query.trim() }, headers: { Authorization: `Bearer ${token}` },
      });
      setResults(res.data.accounts || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Ricerca non disponibile"); }
    finally { setBusy(false); }
  };
  return <div className="space-y-2">
    <label htmlFor={`${prefix}-search`} className="text-sm font-semibold text-slate-700">{label}</label>
    <div className="flex gap-2">
      <input id={`${prefix}-search`} data-testid={`${prefix}-search`} value={query} onChange={e => setQuery(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") search(); }} placeholder="Nome o email (min. 3 caratteri)"
        className="min-w-0 flex-1 border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-600 outline-none" />
      <button type="button" data-testid={`${prefix}-search-button`} disabled={busy || query.trim().length < 3}
        onClick={search} className="bg-teal-700 text-white px-3 disabled:opacity-40 hover:bg-teal-800" title="Cerca account">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</button>
    </div>
    {results.length > 0 && <div className="border border-slate-200 divide-y max-h-48 overflow-y-auto" data-testid={`${prefix}-results`}>
      {results.map(account => <button type="button" key={account.id} data-testid={`${prefix}-result-${account.id}`}
        onClick={() => { onSelect(account); setResults([]); setQuery(""); }}
        className="block w-full text-left px-3 py-2 hover:bg-teal-50 text-sm break-all">
        <strong>{account.nome || "Admin"}</strong> · {account.email}
      </button>)}
    </div>}
    {results.length === 0 && busy === false && query.length >= 3 && <span className="sr-only" data-testid={`${prefix}-no-results`}>Ricerca pronta</span>}
  </div>;
};

export default function CompanyAccountsPanel({ token, societies = [] }) {
  const [sid, setSid] = useState("");
  const [members, setMembers] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [secondary, setSecondary] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [unlinkId, setUnlinkId] = useState("");
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const loadMembers = useCallback(async (societaId) => {
    if (!societaId) { setMembers([]); return; }
    try {
      const res = await axios.get(`${API}/superadmin/societies/${societaId}/members`, { headers });
      setMembers(res.data.members || []);
    } catch (err) { setMembers([]); toast.error(err.response?.data?.detail || "Impossibile caricare gli utenti"); }
  }, [token]);
  useEffect(() => { loadMembers(sid); }, [sid, loadMembers]);

  const act = async (action, fn) => {
    setBusy(true);
    try { await fn(); toast.success(action); await loadMembers(sid); }
    catch (err) { toast.error(err.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  const link = () => act("Amministratore collegato", async () => {
    await axios.post(`${API}/superadmin/societies/${sid}/members`, { user_id: candidate.id }, { headers });
    setCandidate(null);
  });
  const unlink = () => act("Accesso rimosso", async () => {
    await axios.delete(`${API}/superadmin/societies/${sid}/members/${unlinkId}`, { headers });
    setUnlinkId("");
  });
  const merge = () => act("Account uniti", async () => {
    await axios.post(`${API}/superadmin/accounts/merge`, {
      primary_id: primary.id, secondary_id: secondary.id, confirm_email: confirmEmail,
    }, { headers });
    setPrimary(null); setSecondary(null); setConfirmEmail("");
  });

  return <div className="space-y-12" data-testid="company-accounts-panel">
    <header><span className="text-xs font-bold uppercase text-teal-700">Accessi</span>
      <h1 className="text-3xl font-black text-slate-900 mt-1">Account e società</h1></header>
    <section className="space-y-5" aria-label="Utenti dell'abbonamento">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-5 w-5" /> Utenti dell'abbonamento</h2>
      <select data-testid="account-company-select" value={sid} onChange={e => { setSid(e.target.value); setCandidate(null); }}
        className="w-full p-3 border border-slate-300 bg-white text-sm max-w-lg">
        <option value="">Scegli una società</option>
        {societies.map(s => <option key={s.id} value={s.id}>{s.denominazione || s.nome || s.ragione_sociale} · {s.partita_iva || "P.IVA —"}</option>)}
      </select>
      {sid && <>
        <div className="divide-y border-y border-slate-200" data-testid="account-member-list">
          {members.map(member => <div key={member.id} className="flex flex-wrap items-center gap-2 py-3 text-sm min-w-0" data-testid={`account-member-${member.id}`}>
            <Users className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <span className="min-w-0 flex-1 break-words"><strong>{member.nome || member.email || member.id}</strong> <span className="text-slate-500">· {member.ruolo} {member.attivo === false ? "· disattivato" : ""}</span></span>
            {member.account_id && member.attivo !== false && (unlinkId === member.id ? <span className="flex gap-2">
              <button type="button" data-testid={`account-unlink-confirm-${member.id}`} disabled={busy} onClick={unlink} className="text-red-700 font-semibold hover:underline">Conferma</button>
              <button type="button" data-testid={`account-unlink-cancel-${member.id}`} onClick={() => setUnlinkId("")} className="text-slate-500 hover:underline">Annulla</button>
            </span> : <button type="button" data-testid={`account-unlink-${member.id}`} onClick={() => setUnlinkId(member.id)} className="text-red-700 inline-flex gap-1 items-center hover:underline"><Unlink className="h-4 w-4" /> Scollega</button>)}
          </div>)}
        </div>
        <AccountSearch token={token} label="Collega un account esistente come admin" prefix="link-account" onSelect={setCandidate} />
        {candidate && <div className="flex flex-wrap gap-3 items-center text-sm" data-testid="link-account-candidate"><strong>{candidate.nome} · {candidate.email}</strong>
          <button type="button" data-testid="link-account-confirm" disabled={busy} onClick={link} className="bg-teal-700 text-white px-4 py-2 inline-flex gap-2 items-center disabled:opacity-50"><Link2 className="h-4 w-4" /> Collega</button></div>}
      </>}
    </section>
    <section className="border-t border-slate-200 pt-8 space-y-5" aria-label="Fusione account">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><GitMerge className="h-5 w-5" /> Unisci due account</h2>
      <p className="text-sm text-slate-500">Il principale resta attivo; l'accesso al secondario viene disattivato. Società, abbonamenti e fatture restano separati.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><AccountSearch token={token} label="Account da conservare" prefix="merge-primary" onSelect={setPrimary} />{primary && <p data-testid="merge-primary-selected" className="text-sm text-teal-700 mt-2 break-all">{primary.email}</p>}</div>
        <div><AccountSearch token={token} label="Account da disattivare" prefix="merge-secondary" onSelect={setSecondary} />{secondary && <p data-testid="merge-secondary-selected" className="text-sm text-teal-700 mt-2 break-all">{secondary.email}</p>}</div>
      </div>
      {primary && secondary && <><label htmlFor="merge-confirm" className="block text-sm text-slate-700">Per confermare, scrivi l'email del secondario: <strong>{secondary.email}</strong></label>
        <input id="merge-confirm" data-testid="merge-confirm-email" value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} className="block w-full max-w-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
        <button type="button" data-testid="merge-accounts-submit" disabled={busy || primary.id === secondary.id || confirmEmail.toLowerCase() !== secondary.email?.toLowerCase()} onClick={merge} className="bg-slate-900 text-white px-5 py-2 disabled:opacity-40 hover:bg-slate-700 transition-colors">Unisci account</button></>}
    </section>
  </div>;
}